import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckSquare, ExternalLink, Bookmark, ArrowRight, Package, Star, Smartphone, Share, Compass } from 'lucide-react';
import PhaseIntroCard from '@/features/dashboard/PhaseIntroCard';

import { useAuth } from '@/features/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const FB_URL = 'https://www.facebook.com/groups/842778640733978';

const Phase1 = () => {
  const { user, updateUser } = useAuth();
  const [fbClicked, setFbClicked] = useState(false);
  const [initialized, setInitialized] = useState(false);

  if (!user) return null;

  // Auto-mark welcome box on first render
  if (!initialized) {
    setInitialized(true);
    if (!user.phase1Complete[1]) {
      const updated = [...user.phase1Complete];
      updated[1] = true;
      updateUser({ phase1Complete: updated });
    }
  }

  const toggleTask = (index: number) => {
    if (index === 1) return; // Welcome box: auto-checked, no toggle
    const updated = [...user.phase1Complete];
    updated[index] = !updated[index];
    updateUser({ phase1Complete: updated });
  };

  const handleFbClick = () => {
    window.open(FB_URL, '_blank', 'noopener');
    setFbClicked(true);
  };

  const handleBookmark = () => {
    if (!user.phase1Complete[2]) {
      const updated = [...user.phase1Complete];
      updated[2] = true;
      updateUser({ phase1Complete: updated });
    }
  };

  const allComplete = user.phase1Complete.every(Boolean);

  return (
    <div>
      <PhaseIntroCard
        phase={1}
        title="Welcome"
        icon={Compass}
        description="Welcome to First Deal! This dashboard is your guide through every step of the journey, so you're never wondering what's next. Complete the steps below to move into Phase 2: Fast Track."
      />

      <div className="space-y-6">
        {/* Card 1: Welcome Box */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0, duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
          className="bg-gradient-to-br from-white to-[#FAF8F5] border border-border rounded-2xl p-5 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 relative overflow-hidden"
        >
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-gold/20 text-gold flex items-center justify-center shrink-0 shadow-sm border border-gold/20">
              <Package className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2.5 mb-3">
                <h3 className="text-[19px] font-semibold text-foreground leading-tight">Your Welcome Box Is On The Way!</h3>
                <Star className="w-[18px] h-[18px] text-gold fill-gold drop-shadow-sm" />
              </div>
              <p className="text-muted-foreground text-[14.5px] leading-relaxed mb-3 max-w-2xl">
                We've shipped your exclusive First Deal Welcome Box. Keep an eye on your mailbox — it includes everything you need to kick off your journey. No action required here, just excitement!
              </p>
              <p className="text-[13px] text-muted-foreground/70 italic mb-5 font-serif">Please allow up to 2 weeks for delivery.</p>

              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg bg-gold/10 border border-gold/20 text-[#8B6E4A]">
                <div className="w-2 h-2 rounded-full bg-[#8B6E4A] animate-pulse" />
                <Package className="w-[14px] h-[14px]" />
                <span className="text-[13px] font-semibold tracking-wide uppercase">Shipping in progress</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Card 2: Join Facebook Group */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08, duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
          className={cn(
            'border rounded-2xl p-5 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 hover:border-border/80 transition-[transform,box-shadow] duration-200',
            user.phase1Complete[0] ? 'bg-gold/5 border-gold/30' : 'bg-white border-border',
          )}
        >
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <div className={cn(
              'w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm',
              user.phase1Complete[0] ? 'bg-gold/20 text-gold border border-gold/25' : 'bg-[#EDE9E4] text-muted-foreground border border-border',
            )}>
              <ExternalLink className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className={cn('text-[19px] font-semibold leading-tight mb-2', user.phase1Complete[0] ? 'text-gold' : 'text-foreground')}>
                Join the First Deal Facebook Group
              </h3>
              <p className="text-muted-foreground text-[14.5px] leading-relaxed mb-6 max-w-2xl">
                Connect with fellow investors, get support, and stay up to date with the community.
              </p>

              {!user.phase1Complete[0] ? (
                <>
                  <div className="flex flex-wrap gap-4">
                    <button
                      onClick={handleFbClick}
                      className="bg-gold hover:bg-gold/90 text-primary-foreground px-6 py-2.5 rounded-xl text-[14px] font-semibold transition-[transform,box-shadow,opacity] duration-150 flex items-center gap-2.5 shadow-sm hover:shadow hover:-translate-y-0.5 active:scale-[0.97]"
                    >
                      <ExternalLink className="w-4 h-4 opacity-80" />
                      Join Facebook Group
                    </button>
                    <button
                      onClick={() => { setFbClicked(true); toggleTask(0); }}
                      className="bg-white hover:bg-[#F8F6F4] text-foreground border border-[#D5D1CB] px-6 py-2.5 rounded-xl text-[14px] font-semibold transition-colors duration-150 flex items-center gap-2.5 hover:border-[#B5B0AA] active:scale-[0.97]"
                    >
                      <CheckSquare className="w-4 h-4 text-muted-foreground" />
                      I've Joined
                    </button>
                  </div>
                  {fbClicked && (
                    <p className="text-xs text-muted-foreground mt-3">
                      Once you've joined, click "I've Joined" to mark this step complete.
                    </p>
                  )}
                </>
              ) : (
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold/80 bg-gold/10 px-2.5 py-1 rounded-full">
                  <CheckSquare className="h-3 w-3" />
                  Joined
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Card 3: Save App on Mobile */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.16, duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
          className={cn(
            'border rounded-2xl p-5 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 hover:border-border/80 transition-[transform,box-shadow] duration-200',
            user.phase1Complete[2] ? 'bg-gold/5 border-gold/30' : 'bg-white border-border',
          )}
        >
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            <div className={cn(
              'w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-sm',
              user.phase1Complete[2] ? 'bg-gold/20 text-gold border border-gold/25' : 'bg-[#EDE9E4] text-muted-foreground border border-border',
            )}>
              <Bookmark className={cn('w-6 h-6', user.phase1Complete[2] && 'fill-gold')} />
            </div>
            <div className="flex-1">
              <h3 className={cn('text-[19px] font-semibold leading-tight mb-2', user.phase1Complete[2] ? 'text-gold' : 'text-foreground')}>
                Save The App On Mobile
              </h3>
              <p className="text-muted-foreground text-[14.5px] leading-relaxed mb-6 max-w-2xl">
                Add this dashboard to your phone's home screen so it works just like a real app — quick access, no browser needed.
              </p>

              {!user.phase1Complete[2] ? (
                <>
                  <div className="space-y-3 mb-6">
                    {[
                      { step: 1, icon: <Smartphone className="w-4 h-4 text-muted-foreground" />, text: 'Open this dashboard in your smartphone browser by navigating to firstdeal.com and clicking login.' },
                      { step: 2, icon: <Share className="w-4 h-4 text-muted-foreground" />, text: 'Tap the share icon and select "Add to Home Screen"' },
                    ].map(({ step, icon, text }) => (
                      <div key={step} className="bg-[#FAF8F5] rounded-xl p-4 flex items-start gap-4 border border-border">
                        <div className="w-7 h-7 rounded-lg bg-[#EAE5DF] text-muted-foreground text-xs font-bold flex items-center justify-center shrink-0 shadow-sm mt-0.5">
                          {step}
                        </div>
                        <div className="flex items-center gap-3 text-[14.5px] text-foreground font-medium">
                          <div className="p-1.5 bg-white rounded-md shadow-sm border border-border">
                            {icon}
                          </div>
                          {text}
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleBookmark}
                    className="bg-white hover:bg-[#F8F6F4] text-foreground border border-[#D5D1CB] px-6 py-2.5 rounded-xl text-[14px] font-semibold transition-colors duration-150 flex items-center gap-2.5 hover:border-[#B5B0AA] active:scale-[0.97]"
                  >
                    <CheckSquare className="w-4 h-4 text-muted-foreground" />
                    I've saved it on mobile
                  </button>
                </>
              ) : (
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold/80 bg-gold/10 px-2.5 py-1 rounded-full">
                  <CheckSquare className="h-3 w-3" />
                  App Saved
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {allComplete && (
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => updateUser({ phase: 2 })}
          className="mt-8 flex items-center gap-2 px-6 py-3 gradient-gold text-primary-foreground font-semibold rounded-xl shadow-gold hover:opacity-90 transition-[transform,opacity] duration-150 active:scale-[0.97]"
        >
          Move to Phase 2: Fast Track <ArrowRight className="h-4 w-4" />
        </motion.button>
      )}
    </div>
  );
};

export default Phase1;
